import { ErrorFactory } from '../error-factory'

const createChunkDecoder = () => {
  const decoder = new TextDecoder()

  return (chunk?: Uint8Array): string => {
    if (!chunk) return ''
    return decoder.decode(chunk, { stream: true })
  }
}

interface StreamedResponseOptions {
  abortController?: () => AbortController | null
  onStreamMessage: (message: string) => void
}

export const streamedResponse = async (
  response: Response,
  { abortController, onStreamMessage }: StreamedResponseOptions,
): Promise<string> => {
  const reader = response.body?.getReader()
  const decode = createChunkDecoder()

  if (!reader) {
    throw new Error('Response body is not readable')
  }

  let streamedResponse = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    streamedResponse += decode(value).replace(/{{ping}}/g, '')
    onStreamMessage(streamedResponse)

    if (abortController?.() === null) {
      reader.cancel()
      break
    }
  }

  return streamedResponse
}

const getFilenameFromHeader = (
  contentDisposition: string | null,
): string | null => {
  if (!contentDisposition) return null

  const filenameMatch = contentDisposition.match(
    /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
  )
  if (filenameMatch?.[1]) {
    return filenameMatch[1].replace(/['"]/g, '')
  }

  return null
}

interface FileDownloadOptions {
  onProgress: (progress: number) => void
}

export const fileDownloadResponse = async (
  response: Response,
  { onProgress }: FileDownloadOptions,
): Promise<[Blob, string | null]> => {
  const reader = response.body?.getReader()
  const contentLength = Number(response.headers.get('Content-Length')) || 0
  const contentDisposition = response.headers.get('Content-Disposition')
  const filename = getFilenameFromHeader(contentDisposition)
  let receivedLength = 0

  if (!reader) {
    return ErrorFactory('The response body is empty.', response.status, {})
  }

  const bodyReader = reader

  const stream = new ReadableStream({
    start(controller) {
      function push() {
        bodyReader.read().then(({ done, value }) => {
          if (done) {
            controller.close()
            return
          }

          receivedLength += value.length
          const newProgress = (receivedLength / contentLength) * 100
          onProgress(Math.round(newProgress))

          controller.enqueue(value)
          push()
        })
      }

      push()
    },
  })

  return Promise.all([new Response(stream).blob(), filename])
}
